import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';
import { supabase } from '../config/supabase';

export default function ToolsDetail() {
  const { id } = useParams();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchToolDetails = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('tools')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        setTool(data);
      } catch (error) {
        console.error('Error fetching tool details:', error);
        setError('ไม่สามารถโหลดข้อมูลเครื่องมือได้');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchToolDetails();
    }
  }, [id]);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>{tool ? `${tool.name} - รายละเอียด` : 'รายละเอียดเครื่องมือ'}</title>
      </Helmet>

      <main className="flex-grow container mx-auto px-4 py-12">
        {/* ปุ่มย้อนกลับ */}
        <div className="mb-8">
          <Link 
            to="/tools" 
            className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            กลับไปหน้าเครื่องมือทั้งหมด
          </Link>
        </div>

        {/* แสดงข้อความโหลดหรือข้อผิดพลาด */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-300 p-4 rounded-lg mb-8">
            <p>{error}</p>
          </div>
        )}

        {/* แสดงรายละเอียดเครื่องมือ */}
        {!loading && !error && tool && (
          <div className="bg-gray-800 rounded-xl shadow-lg overflow-hidden p-8">
            <div className="flex items-center mb-6">
              {/* รูปภาพ thumbnail แบบวงกลม */}
              <div className="mr-6 flex-shrink-0">
                <img 
                  src={tool.thumbnail || 'https://via.placeholder.com/120'} 
                  alt={tool.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-purple-500 shadow-lg"
                />
              </div>
              <h1 className="text-3xl font-bold">{tool.name}</h1>
            </div>
            
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-2 text-purple-400">รายละเอียด</h2>
              <div className="text-gray-300 whitespace-pre-line">
                {/* แยกข้อความเป็นบรรทัด */}
                {tool.description.split('\n').map((line, lineIndex) => (
                  <React.Fragment key={`line-${lineIndex}`}>
                    {/* แยกแต่ละบรรทัดเป็นคำ */}
                    {line.split(' ').map((word, wordIndex) => {
                      // ตรวจสอบว่าเป็น URL หรือไม่
                      if (word.match(/^(https?:\/\/|www\.)/i)) {
                        const url = word.startsWith('www.') ? `https://${word}` : word;
                        return (
                          <React.Fragment key={`word-${lineIndex}-${wordIndex}`}>
                            <a 
                              href={url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 hover:underline"
                            >
                              {word}
                            </a>
                            {' '}
                          </React.Fragment>
                        );
                      }
                      return <React.Fragment key={`word-${lineIndex}-${wordIndex}`}>{word}{' '}</React.Fragment>;
                    })}
                    {lineIndex < tool.description.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
            </div>
            
            {tool.usage_instructions && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-2 text-purple-400">วิธีใช้งาน</h2>
                <div className="bg-gray-900 p-4 rounded-lg">
                  <div className="text-gray-300 whitespace-pre-line">
                    {/* แยกข้อความเป็นบรรทัด */}
                    {tool.usage_instructions.split('\n').map((line, lineIndex) => (
                      <React.Fragment key={`line-${lineIndex}`}>
                        {/* แยกแต่ละบรรทัดเป็นคำ */}
                        {line.split(' ').map((word, wordIndex) => {
                          // ตรวจสอบว่าเป็น URL หรือไม่
                          if (word.match(/^(https?:\/\/|www\.)/i)) {
                            const url = word.startsWith('www.') ? `https://${word}` : word;
                            return (
                              <React.Fragment key={`word-${lineIndex}-${wordIndex}`}>
                                <a 
                                  href={url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:text-blue-300 hover:underline"
                                >
                                  {word}
                                </a>
                                {' '}
                              </React.Fragment>
                            );
                          }
                          return <React.Fragment key={`word-${lineIndex}-${wordIndex}`}>{word}{' '}</React.Fragment>;
                        })}
                        {lineIndex < tool.usage_instructions.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            <div className="mt-8">
              <a 
                href={tool.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                เข้าใช้งานเครื่องมือ
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
        )}

        {/* แสดงข้อความเมื่อไม่พบเครื่องมือ */}
        {!loading && !error && !tool && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">ไม่พบเครื่องมือที่คุณต้องการ</p>
            <Link 
              to="/tools" 
              className="inline-block mt-4 text-purple-400 hover:text-purple-300 transition-colors"
            >
              กลับไปหน้าเครื่องมือทั้งหมด
            </Link>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
